import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  useTheme, 
  useMediaQuery,
  Menu,
  MenuItem,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Avatar,
  Tooltip,
  Container
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
// eslint-disable-next-line no-unused-vars
import MenuIcon from '@mui/icons-material/Menu';
import SpeedIcon from '@mui/icons-material/Speed';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LoginIcon from '@mui/icons-material/Login';
import SupportIcon from '@mui/icons-material/Support';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import BuildIcon from '@mui/icons-material/Build';
import AppsIcon from '@mui/icons-material/Apps';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../../context/AuthContext';
import gpmLogo from '../../assets/gpm-logo.png';
import './Header.css';
import authService from "../../services/authService";
import clsx from 'clsx';

const Header = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  
  // Refs for scrolling to sections
  // eslint-disable-next-line no-unused-vars
  const servicesRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const platformsRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const pricingRef = useRef(null);

  // Fetch user profile
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await authService.getCurrentUser();
        if (response && response.user) {
          setProfile(response.user);
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      }
    };

    fetchUserProfile();
  }, []);

  // Get the display name from profile, fallback to user context
  const displayName = profile?.given_name || user?.name || 'User';
  
  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleClose();
    navigate('/');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const isActive = (path) => {
    // Special case for home/support page
    if (path === '/' && (location.pathname === '/' || location.pathname === '/support')) {
      return true;
    }
    return location.pathname === path;
  };

  // Since support page is actually the home page
  const isSupportPage = location.pathname === '/' || location.pathname === '/support';

  // Smooth scroll to section wrapped in useCallback
  const scrollToSection = useCallback((sectionId) => {
    setMobileMenuOpen(false);
    
    // Use setTimeout to ensure navigation is complete before scrolling
    setTimeout(() => {
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
      }
    }, isSupportPage ? 100 : 500);
  }, [isSupportPage, setMobileMenuOpen]);

  // Handle section navigation (check if on support page, otherwise navigate first)
  // eslint-disable-next-line no-unused-vars
  const handleSectionNavigation = (sectionId) => {
    if (!isSupportPage) {
      navigate('/', { state: { scrollTo: sectionId } });
    } else {
      scrollToSection(sectionId);
    }
  };

  // Check for scrollTo in location state when component mounts or updates
  useEffect(() => {
    if (location.state?.scrollTo) {
      const sectionId = location.state.scrollTo;
      scrollToSection(sectionId);
      
      // Clean up the state after scrolling
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate, scrollToSection]);

  // Track active section for navigation
  useEffect(() => {
    if (isSupportPage) {
      const sections = ['services', 'platforms', 'pricing'];
      
      const observerOptions = {
        root: null,
        rootMargin: '-100px 0px -25% 0px', // Consider element in view when it enters the top quarter of screen
        threshold: 0.1
      };
      
      const observerCallback = (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      };
      
      const observer = new IntersectionObserver(observerCallback, observerOptions);
      
      // Observe each section
      sections.forEach(sectionId => {
        const element = document.getElementById(sectionId);
        if (element) {
          observer.observe(element);
        }
      });
      
      return () => {
        // Cleanup
        sections.forEach(sectionId => {
          const element = document.getElementById(sectionId);
          if (element) {
            observer.unobserve(element);
          }
        });
      };
    }
  }, [isSupportPage, location.pathname]);

  const renderMobileMenu = () => (
    <Drawer
      anchor="right"
      open={mobileMenuOpen}
      onClose={toggleMobileMenu}
      PaperProps={{
        sx: { width: 280, padding: 0, borderRadius: 0 }
      }}
    >
      <List sx={{ px: 2, pt: 2 }}>
        <ListItem disableGutters sx={{ justifyContent: 'flex-end', mb: 1 }}>
          <Tooltip title="Close menu">
            <IconButton 
              aria-label="Close menu" 
              onClick={toggleMobileMenu} 
              size="small"
              className="mobile-close-btn"
              sx={{ 
                p: 1, 
                transition: "transform 0.25s ease-in-out, background-color 0.25s ease", 
                "&:hover": { transform: "rotate(90deg)" } 
              }}
            >
              <CloseIcon color="primary" fontSize="small" />
            </IconButton>
          </Tooltip>
        </ListItem>
        <ListItem 
          button 
          onClick={() => handleNavigation('/analyzer')}
          selected={isActive('/analyzer')}
          sx={{ borderRadius: 2, mb: 1 }}
        >
          <ListItemIcon>
            <SpeedIcon color={isActive('/analyzer') ? 'primary' : 'inherit'} />
          </ListItemIcon>
          <ListItemText primary="Website Analyzer" />
        </ListItem>
        
        <ListItem 
          button 
          onClick={() => handleNavigation('/')}
          selected={isActive('/')}
          sx={{ borderRadius: 2, mb: 1 }}
        >
          <ListItemIcon>
            <SupportIcon color={isActive('/') ? 'primary' : 'inherit'} />
          </ListItemIcon>
          <ListItemText primary="Support & Fixes" />
        </ListItem>
        
        {isSupportPage && (
          <>
            <Divider sx={{ my: 1.5 }} className="support-section-divider" />
            <Typography 
              variant="subtitle2" 
              sx={{ px: 1, py: 0.5, fontSize: '0.8rem', fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px' }}
            >
              Support Page Sections
            </Typography>
            <Box className="support-submenu">
              <ListItem 
                button 
                onClick={() => scrollToSection('services')} 
                selected={activeSection === 'services'}
                sx={{ borderRadius: 2, mb: 0.5, pl: 3 }}
              >
                <ListItemIcon>
                  <BuildIcon color={activeSection === 'services' ? 'primary' : 'inherit'} fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Services" primaryTypographyProps={{ fontSize: '0.95rem' }} />
              </ListItem>
              <ListItem 
                button 
                onClick={() => scrollToSection('platforms')} 
                selected={activeSection === 'platforms'}
                sx={{ borderRadius: 2, mb: 0.5, pl: 3 }}
              >
                <ListItemIcon>
                  <AppsIcon color={activeSection === 'platforms' ? 'primary' : 'inherit'} fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Platforms" primaryTypographyProps={{ fontSize: '0.95rem' }} />
              </ListItem>
              <ListItem 
                button 
                onClick={() => scrollToSection('pricing')} 
                selected={activeSection === 'pricing'}
                sx={{ borderRadius: 2, mb: 1, pl: 3 }}
              >
                <ListItemIcon>
                  <MonetizationOnIcon color={activeSection === 'pricing' ? 'primary' : 'inherit'} fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Pricing" primaryTypographyProps={{ fontSize: '0.95rem' }} />
              </ListItem>
            </Box>
            <Divider sx={{ my: 1.5 }} />
          </>
        )}
        
        {isAuthenticated && (
          <ListItem 
            button 
            onClick={() => handleNavigation('/dashboard')}
            selected={isActive('/dashboard')}
            sx={{ borderRadius: 2, mb: 1 }}
          >
            <ListItemIcon>
              <DashboardIcon color={isActive('/dashboard') ? 'primary' : 'inherit'} />
            </ListItemIcon>
            <ListItemText primary="Dashboard" />
          </ListItem>
        )}
      </List>
      
      <Divider sx={{ my: 1, mx: 2 }} />
      
      {isAuthenticated ? (
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
              {displayName.charAt(0)}
            </Avatar>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              {displayName}
            </Typography>
          </Box>
          <Button 
            variant="outlined" 
            color="primary" 
            fullWidth 
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{ borderRadius: 2 }}
          >
            Sign Out
          </Button>
        </Box>
      ) : (
        <Box sx={{ p: 3 }}>
          <Button 
            variant="contained" 
            color="primary" 
            fullWidth 
            startIcon={<LoginIcon />}
            onClick={() => handleNavigation('/login')}
            sx={{ borderRadius: 2 }}
          >
            Sign In
          </Button>
          {!isAuthenticated && (
            <Button 
              variant="contained" 
              color="secondary" 
              fullWidth 
              startIcon={<RocketLaunchIcon />} 
              onClick={() => handleNavigation('/register')} 
              sx={{ borderRadius: 2, mt: 2 }}
              aria-label="Get Started"
            >
              Get Started
            </Button>
          )}
        </Box>
      )}
    </Drawer>
  );

  return (
    <AppBar position="fixed" color="default" elevation={2} className="header" sx={{ zIndex: 9 }}>
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
          <Link to="/" className="logo-link">
            <Box display="flex" alignItems="center">
              <img 
                src={gpmLogo} 
                alt="Gravity Point Media - Website Performance Analysis Tools" 
                className="logo" 
                height={isMobile ? 36 : 40} 
                style={{ marginRight: isMobile ? '10px' : '16px' }}
                title="Gravity Point Media"
                loading="eager"
                width={isMobile ? 108 : 120}
                itemProp="logo"
              />
            </Box>
          </Link>
          
          {isMobile ? (
            <IconButton 
              edge="end" 
              color="primary" 
              aria-label="menu"
              onClick={toggleMobileMenu}
              sx={{ ml: 1 }}
              className={clsx('hamburger', { open: mobileMenuOpen })}
            >
              <span className="hamburger-bar" />
              <span className="hamburger-bar" />
              <span className="hamburger-bar" />
            </IconButton>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <Button 
                component={Link} 
                to="/analyzer" 
                className={`nav-button ${isActive('/analyzer') ? 'active' : ''}`}
                startIcon={<SpeedIcon />}
              >
                Website Analyzer
              </Button>

              <Button 
                component={Link} 
                to="/" 
                className={`nav-button ${isActive('/') ? 'active' : ''}`}
                startIcon={<SupportIcon />}
              >
                Support & Fixes
              </Button>
              
              {isSupportPage && (
                <Box className="support-nav-section" sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  ml: 1,
                  borderLeft: '1px solid rgba(0,0,0,0.1)',
                  pl: 1
                }}>
                  <Tooltip title="Jump to Services section">
                    <Button 
                      className={`nav-button section-nav ${activeSection === 'services' ? 'section-active' : ''}`}
                      startIcon={<BuildIcon />}
                      onClick={() => scrollToSection('services')}
                      size="small"
                      sx={{ fontSize: '0.9rem' }}
                    >
                      Services
                    </Button>
                  </Tooltip>
                  
                  <Tooltip title="Jump to Platforms section">
                    <Button 
                      className={`nav-button section-nav ${activeSection === 'platforms' ? 'section-active' : ''}`}
                      startIcon={<AppsIcon />}
                      onClick={() => scrollToSection('platforms')}
                      size="small"
                      sx={{ fontSize: '0.9rem' }}
                    >
                      Platforms
                    </Button>
                  </Tooltip>
                  
                  <Tooltip title="Jump to Pricing section">
                    <Button 
                      className={`nav-button section-nav ${activeSection === 'pricing' ? 'section-active' : ''}`}
                      startIcon={<MonetizationOnIcon />}
                      onClick={() => scrollToSection('pricing')}
                      size="small"
                      sx={{ fontSize: '0.9rem' }}
                    >
                      Pricing
                    </Button>
                  </Tooltip>
                </Box>
              )}
              
              {isAuthenticated && (
                <Button 
                  component={Link} 
                  to="/dashboard" 
                  className={`nav-button ${isActive('/dashboard') ? 'active' : ''}`}
                  startIcon={<DashboardIcon />}
                >
                  Dashboard
                </Button>
              )}
              
              {!isAuthenticated && (
                <Button 
                  component={Link} 
                  to="/register" 
                  className="auth-button" 
                  variant="outlined" 
                  color="secondary" 
                  startIcon={<RocketLaunchIcon />} 
                  sx={{ ml: 1 }}
                  aria-label="Get Started"
                >
                  Get Started
                </Button>
              )}
              
              {!isAuthenticated ? (
                <Button 
                  component={Link} 
                  to="/login" 
                  className="auth-button"
                  variant="contained"
                  color="primary"
                  startIcon={<LoginIcon />}
                >
                  Sign In
                </Button>
              ) : (
                <>
                  <Tooltip title="Account settings">
                    <IconButton
                      aria-label="account of current user"
                      aria-controls="menu-appbar"
                      aria-haspopup="true"
                      onClick={handleMenu}
                      color="primary"
                      sx={{ ml: 1, border: `1px solid ${theme.palette.primary.light}`, borderRadius: '50%' }}
                    >
                      <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main, fontSize: '0.9rem' }}>
                        {displayName.charAt(0)}
                      </Avatar>
                    </IconButton>
                  </Tooltip>
                  <Menu
                    id="menu-appbar"
                    anchorEl={anchorEl}
                    keepMounted
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    PaperProps={{
                      elevation: 2,
                      sx: {
                        borderRadius: 2,
                        minWidth: 180,
                        mt: 1,
                        '& .MuiList-root': {
                          py: 1
                        }
                      }
                    }}
                  >
                    <MenuItem disabled sx={{ opacity: 1, fontWeight: 500, color: theme.palette.text.primary }}>
                      {displayName}
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={handleLogout} sx={{ color: theme.palette.error.main }}>
                      <ListItemIcon>
                        <LogoutIcon fontSize="small" sx={{ color: theme.palette.error.main }} />
                      </ListItemIcon>
                      Sign Out
                    </MenuItem>
                  </Menu>
                </>
              )}
            </Box>
          )}
        </Toolbar>
      </Container>
      {renderMobileMenu()}
    </AppBar>
  );
};

export default Header;